import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { RaEmptyComponent } from 'ra-ui';
import { QuoteItemController } from 'src/app/domain/controllers/quote-item.controller';
import { QuoteTaxeController } from 'src/app/domain/controllers/quote-taxe.controller';
import { QuoteItemEntity } from 'src/app/domain/entities/quotes/quote-item.entity';
import { QuoteTaxeEntity } from 'src/app/domain/entities/quotes/quote-taxe.entity';
import { RaButtonComponent } from 'src/app/shared/components/ra-button/ra-button.component';
import { RaInputComponent } from 'src/app/shared/components/ra-input/ra-input.component';
import { RaLoadingComponent } from 'src/app/shared/components/ra-loading/ra-loading.component';
import { RaModalComponent } from 'src/app/shared/components/ra-modal/ra-modal.component';
import { RaCurrencyPipe } from 'src/app/shared/pipes/ra-currency.pipe';
import { UnitOfMeasurementFormatPipe } from 'src/app/shared/pipes/unit-of-measurement-format.pipe';

/** Qual catálogo da empresa o modal está listando. */
export type QuoteCatalogMode = 'items' | 'taxes';

/**
 * Catálogo da empresa (produtos/serviços ou taxas) para inserir no orçamento.
 *
 * Espelha o passo "Do catálogo" do app, com uma diferença deliberada: o app pergunta a
 * quantidade num sheet à parte e volta para a lista; aqui a quantidade é um campo na
 * própria linha e o modal FICA ABERTO depois de adicionar, porque montar um orçamento
 * costuma pegar vários itens seguidos e reabrir o catálogo a cada um custa dois cliques.
 */
@Component({
    selector: 'modal-quote-catalog',
    templateUrl: 'modal-quote-catalog.component.html',
    styleUrl: 'modal-quote-catalog.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslatePipe,
        RaModalComponent,
        RaButtonComponent,
        RaInputComponent,
        RaLoadingComponent,
        RaEmptyComponent,
        RaCurrencyPipe,
        UnitOfMeasurementFormatPipe,
    ],
})
export class ModalQuoteCatalogComponent implements OnInit {
    private _itemController = inject(QuoteItemController);
    private _taxeController = inject(QuoteTaxeController);
    private _destroyRef = inject(DestroyRef);

    public mode = input.required<QuoteCatalogMode>();
    /** Loading do documento — trava as linhas enquanto um add está em voo. */
    public loading = input<boolean>(false);
    public currencyCode = input<string | null | undefined>();
    public locale = input<string>('pt-BR');

    public close = output<void>();
    public addItem = output<QuoteItemEntity>();
    public addTaxe = output<QuoteTaxeEntity>();

    public isItems = computed(() => this.mode() === 'items');

    public fetching = signal(true);
    public items = signal<QuoteItemEntity[]>([]);
    public taxes = signal<QuoteTaxeEntity[]>([]);

    public searchControl = new FormControl<string>('', { nonNullable: true });
    private _search = toSignal(this.searchControl.valueChanges, { initialValue: '' });

    /**
     * Um FormControl de quantidade por linha, criado sob demanda e memoizado por id: o
     * `ra-input` exige um control, e recriá-lo a cada change-detection perderia o que foi
     * digitado. Default 1 — o caso comum é adicionar uma unidade.
     */
    private _quantityControls = new Map<string, FormControl<number | null>>();

    public filteredItems = computed(() => {
        const term = this._normalized(this._search());
        if (!term) return this.items();
        return this.items().filter(item => this._normalized(item.name).includes(term));
    });

    public filteredTaxes = computed(() => {
        const term = this._normalized(this._search());
        if (!term) return this.taxes();
        return this.taxes().filter(taxe => this._normalized(taxe.name).includes(term));
    });

    /** Catálogo vazio x busca sem resultado são estados diferentes (mensagens diferentes). */
    public catalogIsEmpty = computed(() =>
        this.isItems() ? this.items().length === 0 : this.taxes().length === 0
    );

    public searchHasNoResult = computed(() =>
        this.isItems()
            ? this.filteredItems().length === 0 && this.items().length > 0
            : this.filteredTaxes().length === 0 && this.taxes().length > 0
    );

    ngOnInit(): void {
        if (this.isItems()) {
            this._itemController
                .getByCompanyId()
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe({
                    next: list => {
                        this.items.set(list ?? []);
                        this.fetching.set(false);
                    },
                    error: () => this.fetching.set(false),
                });
            return;
        }

        this._taxeController
            .getByCompanyId()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe({
                next: list => {
                    this.taxes.set(list ?? []);
                    this.fetching.set(false);
                },
                error: () => this.fetching.set(false),
            });
    }

    public qtyControl(item: QuoteItemEntity): FormControl<number | null> {
        const existing = this._quantityControls.get(item._id);
        if (existing) return existing;

        const control = new FormControl<number | null>(1);
        this._quantityControls.set(item._id, control);
        return control;
    }

    public canAddItem(item: QuoteItemEntity): boolean {
        const quantity = this.qtyControl(item).value;
        return !this.loading() && typeof quantity === 'number' && quantity > 0;
    }

    /** Alíquota da taxa do catálogo, no formato do locale do documento. */
    public formatPercent(value: number | null | undefined): string {
        const amount = typeof value === 'number' && Number.isFinite(value) ? value : 0;
        const formatted = new Intl.NumberFormat(this.locale() || 'pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
        return `${formatted}%`;
    }

    /**
     * Monta o item do DOCUMENTO a partir da linha do catálogo. `catalogItemId` guarda a
     * origem (o catálogo pode mudar depois sem mexer no orçamento já montado); o `_id` do
     * catálogo NÃO vai junto, senão o subitem do documento nasceria com id de outra coleção.
     */
    public onAddItem(item: QuoteItemEntity): void {
        if (!this.canAddItem(item)) return;

        const newItem = new QuoteItemEntity();
        newItem.name = item.name;
        newItem.type = item.type;
        newItem.unitOfMeasurement = item.unitOfMeasurement;
        newItem.unitPrice = item.unitPrice ?? 0;
        newItem.quantity = this.qtyControl(item).value as number;
        newItem.catalogItemId = item._id;

        this.addItem.emit(newItem);
    }

    public onAddTaxe(taxe: QuoteTaxeEntity): void {
        if (this.loading()) return;

        const newTaxe = new QuoteTaxeEntity();
        newTaxe.name = taxe.name;
        // O modo VAI junto: uma taxa percentual do catálogo que virasse fixa no documento
        // passaria a somar a alíquota como dinheiro.
        newTaxe.mode = taxe.mode ?? 'fixed';
        newTaxe.price = taxe.price ?? 0;
        newTaxe.catalogItemId = taxe._id;

        this.addTaxe.emit(newTaxe);
    }

    /** Busca sem acento e sem caixa — "irrigacao" acha "Irrigação". */
    private _normalized(value: string | null | undefined): string {
        return (value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toLowerCase();
    }
}
